import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    AcademicCapIcon,
    PlusIcon,
    EyeIcon,
    PencilSquareIcon,
    CheckCircleIcon,
    XCircleIcon,
    MapPinIcon,
    ArrowPathIcon,
    XMarkIcon,
    BuildingStorefrontIcon,
    EnvelopeIcon,
    PhoneIcon,
    BanknotesIcon,
    PhotoIcon,
    TrashIcon
} from "@heroicons/react/24/outline";
import SuperAdminNavbar from "../../components/SuperAdminNav";
import LoadingPage from "../../components/Loader";
import { showSuccessNotification, showErrorNotification } from '../../components/NotificationManager';

const UniversityManagement = () => {
    const [universities, setUniversities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('universities');

    // University states
    const [showAddUniversityModal, setShowAddUniversityModal] = useState(false);
    const [addingUniversity, setAddingUniversity] = useState(false);
    const [newUniversity, setNewUniversity] = useState({
        name: '',
        state: '',
        isActive: true
    });
    const [showEditUniversityModal, setShowEditUniversityModal] = useState(false);
    const [editingUniversity, setEditingUniversity] = useState(false);
    const [editUniversity, setEditUniversity] = useState(null);
    const [deletingUniversities, setDeletingUniversities] = useState(new Set());

    // Restaurant states
    const [showAddRestaurantModal, setShowAddRestaurantModal] = useState(false);
    const [addingRestaurant, setAddingRestaurant] = useState(false);
    const [newRestaurant, setNewRestaurant] = useState({
        name: "",
        email: "",
        university: "",
        location: "",
        description: "",
        contactNumber: "",
        imageUrl: "",
        bankName: "",
        accountNumber: "",
        accountHolder: ""
    });
    const [selectedImage, setSelectedImage] = useState(null);

    // Landmarks management states
    const [showLandmarksModal, setShowLandmarksModal] = useState(false);
    const [viewLandmarks, setViewLandmarks] = useState([]);
    const [selectedUniversity, setSelectedUniversity] = useState(null);
    const [editLandmarksId, setEditLandmarksId] = useState(null);
    const [landmarksInput, setLandmarksInput] = useState("");
    const [isUpdatingLandmarks, setIsUpdatingLandmarks] = useState(false);

    // Fetch universities
    const fetchUniversities = useCallback(async (showRefreshToast = false) => {
        try {
            if (showRefreshToast) setRefreshing(true);

            const response = await axios.get("https://mongobyte-hc73.onrender.com/api/v1/superadmin/universities", {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                timeout: 30000
            });
            setUniversities(response.data.universities || []);

            if (showRefreshToast) {
                showSuccessNotification("Universities refreshed successfully!");
            }
        } catch (error) {
            console.error("Error fetching universities:", error);
            showErrorNotification("Failed to fetch universities");
            setUniversities([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchUniversities();
    }, [fetchUniversities]);

    // Add new university
    const handleAddUniversity = async () => {
        if (!newUniversity.name.trim() || !newUniversity.state.trim()) {
            showErrorNotification("Please fill in all required fields");
            return;
        }

        setAddingUniversity(true);
        try {
            const response = await axios.post(
                "https://mongobyte-hc73.onrender.com/api/v1/superadmin/universities",
                newUniversity,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.data && response.data.university) {
                showSuccessNotification(response.data.message || "University added successfully!");
                setNewUniversity({ name: '', state: '', isActive: true });
                setShowAddUniversityModal(false);
                fetchUniversities();
            } else {
                showErrorNotification("Failed to add university");
            }
        } catch (error) {
            console.error("Error adding university:", error);
            showErrorNotification(error.response?.data?.message || "Failed to add university");
        } finally {
            setAddingUniversity(false);
        }
    };

    // Update university
    const handleUpdateUniversity = async () => {
        if (!editUniversity.name.trim() || !editUniversity.state.trim()) {
            showErrorNotification("Please fill in all required fields");
            return;
        }

        setEditingUniversity(true);
        try {
            const response = await axios.put(
                `https://mongobyte-hc73.onrender.com/api/v1/superadmin/universities/${editUniversity._id}`,
                {
                    name: editUniversity.name,
                    state: editUniversity.state,
                    isActive: editUniversity.isActive
                },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.data && response.data.university) {
                showSuccessNotification(response.data.message || "University updated successfully!");
                setShowEditUniversityModal(false);
                setEditUniversity(null);
                fetchUniversities();
            } else {
                showErrorNotification("Failed to update university");
            }
        } catch (error) {
            console.error("Error updating university:", error);
            showErrorNotification(error.response?.data?.message || "Failed to update university");
        } finally {
            setEditingUniversity(false);
        }
    };

    // Delete university
    const handleDeleteUniversity = async (university) => {
        const confirmMessage = `Are you sure you want to delete "${university.name}"?\n\nThis action cannot be undone and will fail if the university has associated restaurants or users.`;
        
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setDeletingUniversities(prev => new Set(prev).add(university._id));
        try {
            const response = await axios.delete(
                `https://mongobyte-hc73.onrender.com/api/v1/superadmin/universities/${university._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.data) {
                showSuccessNotification(response.data.message || "University deleted successfully!");
                fetchUniversities();
            }
        } catch (error) {
            console.error("Error deleting university:", error);
            const errorMessage = error.response?.data?.message || "Failed to delete university";
            showErrorNotification(errorMessage);
        } finally {
            setDeletingUniversities(prev => {
                const newSet = new Set(prev);
                newSet.delete(university._id);
                return newSet;
            });
        }
    };

    // Add new restaurant
    const handleAddRestaurant = async () => {
        if (!newRestaurant.name.trim() || !newRestaurant.email.trim() || !newRestaurant.university) {
            showErrorNotification("Please fill in all required fields");
            return;
        }

        setAddingRestaurant(true);
        try {
            let imageUrl = newRestaurant.imageUrl;
            if (selectedImage) {
                imageUrl = await uploadImage(selectedImage);
            }

            const response = await axios.post(
                "https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurants",
                { ...newRestaurant, imageUrl },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.data) {
                showSuccessNotification("Restaurant created successfully!");
                setNewRestaurant({
                    name: "",
                    email: "",
                    university: "",
                    location: "",
                    description: "",
                    contactNumber: "",
                    imageUrl: "",
                    bankName: "",
                    accountNumber: "",
                    accountHolder: ""
                });
                setSelectedImage(null);
                setShowAddRestaurantModal(false);
            }
        } catch (error) {
            console.error("Error creating restaurant:", error);
            const errorMessage = error.response?.data?.message || "Failed to create restaurant";
            showErrorNotification(errorMessage);
        } finally {
            setAddingRestaurant(false);
        }
    };

    // Upload image helper
    const uploadImage = async (imageData) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(
                "https://mongobyte-hc73.onrender.com/api/v1/users/upload",
                { image: imageData },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                }
            );
            return response.data.url;
        } catch (error) {
            showErrorNotification("Image upload failed");
            throw error;
        }
    };

    // Handle image selection
    const handleImageChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // View landmarks
    const handleViewLandmarks = async (uni) => {
        setSelectedUniversity(uni);
        setShowLandmarksModal(true);
        try {
            const response = await axios.get(`https://mongobyte-hc73.onrender.com/api/v1/universities/${uni._id}/landmarks`);
            setViewLandmarks(response.data.landmarks || []);
        } catch (error) {
            console.error("Error fetching landmarks:", error);
            setViewLandmarks([]);
        }
    };

    // Edit landmarks
    const handleEditLandmarks = async (uni) => {
        setEditLandmarksId(uni._id);
        setLandmarksInput('');
        try {
            const response = await axios.get(`https://mongobyte-hc73.onrender.com/api/v1/universities/${uni._id}/landmarks`);
            const fetchedLandmarks = response.data.landmarks || [];
            setLandmarksInput(fetchedLandmarks.join(", "));
        } catch (error) {
            console.error("Error fetching landmarks for edit:", error);
        }
    };

    // Save landmarks
    const handleSaveLandmarks = async (universityId) => {
        setIsUpdatingLandmarks(true);
        try {
            const landmarksArr = landmarksInput.split(",").map(l => l.trim()).filter(Boolean);
            await axios.put(
                `https://mongobyte-hc73.onrender.com/api/v1/superadmin/universities/${universityId}/nearest-landmarks`,
                { nearestLandmarks: landmarksArr },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            showSuccessNotification("Landmarks updated successfully!");
            setEditLandmarksId(null);
            setLandmarksInput("");
            fetchUniversities();
        } catch (error) {
            showErrorNotification("Failed to update landmarks");
            console.error("Error updating landmarks:", error);
        } finally {
            setIsUpdatingLandmarks(false);
        }
    };

    if (loading) {
        return <LoadingPage />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-accentwhite">
            <SuperAdminNavbar />

            <div className="container mx-auto px-4 py-8 pb-16 sm:pb-24">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-crust mb-2">University Management Hub</h1>
                        <p className="text-olive-light">Manage universities and set up new restaurants</p>
                    </div>

                    <div className="flex gap-3 mt-4 md:mt-0">
                        <button
                            onClick={() => fetchUniversities(true)}
                            disabled={refreshing}
                            className="flex items-center gap-2 bg-secondary-100 hover:bg-secondary-200 text-crust px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-8 max-w-md">
                    <button
                        onClick={() => setActiveTab('universities')}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === 'universities'
                            ? 'bg-white text-crust shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        <AcademicCapIcon className="w-4 h-4 inline mr-2" />
                        Universities
                    </button>
                    <button
                        onClick={() => setActiveTab('restaurants')}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === 'restaurants'
                            ? 'bg-white text-crust shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        <BuildingStorefrontIcon className="w-4 h-4 inline mr-2" />
                        Add Restaurant
                    </button>
                </div>

                {/* Universities Tab */}
                {activeTab === 'universities' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-crust">Universities</h2>
                            <button
                                onClick={() => setShowAddUniversityModal(true)}
                                className="flex items-center gap-2 bg-cheese hover:bg-yellow-500 text-crust px-4 py-2 rounded-lg font-medium transition-colors shadow-md"
                            >
                                <PlusIcon className="w-4 h-4" />
                                Add University
                            </button>
                        </div>

                        {universities.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-md p-12 text-center">
                                <AcademicCapIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Universities Found</h3>
                                <p className="text-gray-500 mb-6">Get started by adding your first university</p>
                                <button
                                    onClick={() => setShowAddUniversityModal(true)}
                                    className="bg-cheese hover:bg-yellow-500 text-crust px-6 py-3 rounded-lg font-medium transition-colors"
                                >
                                    Add University
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Desktop Table View */}
                                <div className="hidden lg:block bg-white rounded-xl shadow-md overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                                        University
                                                    </th>
                                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                                        State
                                                    </th>
                                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                                        Stats
                                                    </th>
                                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                                        Landmarks
                                                    </th>
                                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {universities.map((uni) => (
                                                    <tr key={uni._id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="w-10 h-10 bg-cheese/20 rounded-full flex items-center justify-center mr-3">
                                                                    <AcademicCapIcon className="w-6 h-6 text-crust" />
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">{uni.name}</div>
                                                                    <div className="text-sm text-gray-500">ID: {uni._id.slice(-6)}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className="text-sm font-medium text-gray-900">{uni.state}</span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {uni.isActive ? (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-cheese text-pepperoni">
                                                                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                                                                    Active
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                                                                    <XCircleIcon className="w-4 h-4 mr-1" />
                                                                    Inactive
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                <div>Restaurants: {uni.stats?.restaurantCount || 0}</div>
                                                                <div>Users: {uni.stats?.userCount || 0}</div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => handleViewLandmarks(uni)}
                                                                    className="inline-flex items-center px-3 py-1 bg-secondary-100 text-crust rounded-lg text-sm font-medium hover:bg-secondary-200 transition-colors"
                                                                >
                                                                    <EyeIcon className="w-4 h-4 mr-1" />
                                                                    View
                                                                </button>
                                                                <button
                                                                    onClick={() => handleEditLandmarks(uni)}
                                                                    className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-medium hover:bg-yellow-200 transition-colors"
                                                                >
                                                                    <PencilSquareIcon className="w-4 h-4 mr-1" />
                                                                    Edit
                                                                </button>
                                                            </div>

                                                            {editLandmarksId === uni._id && (
                                                                <div className="mt-3 space-y-2">
                                                                    <textarea
                                                                        value={landmarksInput}
                                                                        onChange={(e) => setLandmarksInput(e.target.value)}
                                                                        placeholder="Enter landmarks separated by commas"
                                                                        className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none"
                                                                        rows="3"
                                                                    />
                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            onClick={() => handleSaveLandmarks(uni._id)}
                                                                            disabled={isUpdatingLandmarks}
                                                                            className="px-3 py-1 bg-cheese text-white rounded text-sm hover:bg-cheese transition-colors disabled:opacity-50"
                                                                        >
                                                                            {isUpdatingLandmarks ? 'Saving...' : 'Save'}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => {
                                                                                setEditLandmarksId(null);
                                                                                setLandmarksInput("");
                                                                            }}
                                                                            className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        setEditUniversity(uni);
                                                                        setShowEditUniversityModal(true);
                                                                    }}
                                                                    className="inline-flex items-center px-3 py-1 bg-secondary-100 text-crust rounded-lg text-sm font-medium hover:bg-secondary-200 transition-colors"
                                                                >
                                                                    <PencilSquareIcon className="w-4 h-4 mr-1" />
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteUniversity(uni)}
                                                                    disabled={deletingUniversities.has(uni._id)}
                                                                    className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                                                                >
                                                                    {deletingUniversities.has(uni._id) ? (
                                                                        <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
                                                                    ) : (
                                                                        <TrashIcon className="w-4 h-4 mr-1" />
                                                                    )}
                                                                    {deletingUniversities.has(uni._id) ? 'Deleting...' : 'Delete'}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Mobile Card View */}
                                <div className="lg:hidden space-y-4">
                                    {universities.map((uni) => (
                                        <motion.div
                                            key={uni._id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
                                        >
                                            {/* Header */}
                                            <div className="bg-gradient-to-r from-crust to-pepperoni p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                                            <AcademicCapIcon className="w-7 h-7 text-white" />
                                                        </div>
                                                        <div>
                                                            <h3 className="text-lg font-semibold text-white truncate max-w-[200px]">
                                                                {uni.name}
                                                            </h3>
                                                            <p className="text-cheese/80 text-sm">
                                                                {uni.state} • ID: {uni._id.slice(-6)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center space-x-1">
                                                        {uni.isActive ? (
                                                            <div className="bg-cheese rounded-full p-1">
                                                                <CheckCircleIcon className="w-5 h-5 text-white" />
                                                            </div>
                                                        ) : (
                                                            <div className="bg-gray-500 rounded-full p-1">
                                                                <XCircleIcon className="w-5 h-5 text-white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Stats */}
                                            <div className="p-4 bg-gray-50 border-b">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-crust">{uni.stats?.restaurantCount || 0}</div>
                                                        <div className="text-sm text-gray-600">Restaurants</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-crust">{uni.stats?.userCount || 0}</div>
                                                        <div className="text-sm text-gray-600">Users</div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="p-4 space-y-4">
                                                {/* Landmarks Actions */}
                                                <div>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-sm font-medium text-gray-700">Landmarks</span>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleViewLandmarks(uni)}
                                                                className="inline-flex items-center px-2 py-1 bg-secondary-100 text-crust rounded text-xs font-medium hover:bg-secondary-200 transition-colors"
                                                            >
                                                                <EyeIcon className="w-3 h-3 mr-1" />
                                                                View
                                                            </button>
                                                            <button
                                                                onClick={() => handleEditLandmarks(uni)}
                                                                className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium hover:bg-yellow-200 transition-colors"
                                                            >
                                                                <PencilSquareIcon className="w-3 h-3 mr-1" />
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {editLandmarksId === uni._id && (
                                                        <div className="space-y-2">
                                                            <textarea
                                                                value={landmarksInput}
                                                                onChange={(e) => setLandmarksInput(e.target.value)}
                                                                placeholder="Enter landmarks separated by commas"
                                                                className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none"
                                                                rows="3"
                                                            />
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => handleSaveLandmarks(uni._id)}
                                                                    disabled={isUpdatingLandmarks}
                                                                    className="flex-1 py-2 bg-cheese text-white rounded-lg text-sm font-medium hover:bg-cheese transition-colors disabled:opacity-50"
                                                                >
                                                                    {isUpdatingLandmarks ? 'Saving...' : 'Save'}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditLandmarksId(null);
                                                                        setLandmarksInput("");
                                                                    }}
                                                                    className="flex-1 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Main Actions */}
                                                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                                                    <button
                                                        onClick={() => {
                                                            setEditUniversity(uni);
                                                            setShowEditUniversityModal(true);
                                                        }}
                                                        className="flex items-center justify-center space-x-2 py-3 bg-crust text-white rounded-lg font-medium hover:bg-crust/90 transition-colors"
                                                    >
                                                        <PencilSquareIcon className="w-4 h-4" />
                                                        <span>Edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUniversity(uni)}
                                                        disabled={deletingUniversities.has(uni._id)}
                                                        className="flex items-center justify-center space-x-2 py-3 bg-pepperoni text-white rounded-lg font-medium hover:bg-pepperoni/90 transition-colors disabled:opacity-50"
                                                    >
                                                        {deletingUniversities.has(uni._id) ? (
                                                            <>
                                                                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                                                <span>Deleting...</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TrashIcon className="w-4 h-4" />
                                                                <span>Delete</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Add Restaurant Tab */}
                {activeTab === 'restaurants' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-crust">Set Up New Restaurant</h2>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Basic Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Restaurant Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={newRestaurant.name}
                                            onChange={(e) => setNewRestaurant(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="Enter restaurant name"
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            value={newRestaurant.email}
                                            onChange={(e) => setNewRestaurant(prev => ({ ...prev, email: e.target.value }))}
                                            placeholder="restaurant@example.com"
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            University *
                                        </label>
                                        <select
                                            value={newRestaurant.university}
                                            onChange={(e) => setNewRestaurant(prev => ({ ...prev, university: e.target.value }))}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                        >
                                            <option value="">Select University</option>
                                            {universities.filter(uni => uni.isActive).map((uni) => (
                                                <option key={uni._id} value={uni._id}>
                                                    {uni.name} - {uni.state}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Location
                                        </label>
                                        <input
                                            type="text"
                                            value={newRestaurant.location}
                                            onChange={(e) => setNewRestaurant(prev => ({ ...prev, location: e.target.value }))}
                                            placeholder="Restaurant location/address"
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Contact Number
                                        </label>
                                        <input
                                            type="tel"
                                            value={newRestaurant.contactNumber}
                                            onChange={(e) => setNewRestaurant(prev => ({ ...prev, contactNumber: e.target.value }))}
                                            placeholder="Phone number"
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                {/* Additional Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Description
                                        </label>
                                        <textarea
                                            value={newRestaurant.description}
                                            onChange={(e) => setNewRestaurant(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Brief description of the restaurant"
                                            rows="3"
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent resize-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Restaurant Image
                                        </label>
                                        <div className="flex items-center space-x-4">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                                id="restaurant-image"
                                            />
                                            <label
                                                htmlFor="restaurant-image"
                                                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg cursor-pointer transition-colors"
                                            >
                                                <PhotoIcon className="w-5 h-5" />
                                                Choose Image
                                            </label>
                                            {selectedImage && (
                                                <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-300">
                                                    <img
                                                        src={selectedImage}
                                                        alt="Preview"
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="border-t pt-4">
                                        <h4 className="text-md font-medium text-gray-900 mb-3">Banking Information</h4>

                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Bank Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={newRestaurant.bankName}
                                                    onChange={(e) => setNewRestaurant(prev => ({ ...prev, bankName: e.target.value }))}
                                                    placeholder="Bank name"
                                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Account Number
                                                </label>
                                                <input
                                                    type="text"
                                                    value={newRestaurant.accountNumber}
                                                    onChange={(e) => setNewRestaurant(prev => ({ ...prev, accountNumber: e.target.value }))}
                                                    placeholder="Account number"
                                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Account Holder Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={newRestaurant.accountHolder}
                                                    onChange={(e) => setNewRestaurant(prev => ({ ...prev, accountHolder: e.target.value }))}
                                                    placeholder="Account holder name"
                                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
                                <button
                                    onClick={() => {
                                        setNewRestaurant({
                                            name: "",
                                            email: "",
                                            university: "",
                                            location: "",
                                            description: "",
                                            contactNumber: "",
                                            imageUrl: "",
                                            bankName: "",
                                            accountNumber: "",
                                            accountHolder: ""
                                        });
                                        setSelectedImage(null);
                                    }}
                                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Clear Form
                                </button>
                                <button
                                    onClick={handleAddRestaurant}
                                    disabled={addingRestaurant || !newRestaurant.name.trim() || !newRestaurant.email.trim() || !newRestaurant.university}
                                    className="px-6 py-3 bg-cheese hover:bg-yellow-500 text-crust font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {addingRestaurant ? (
                                        <>
                                            <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                            Creating Restaurant...
                                        </>
                                    ) : (
                                        <>
                                            <BuildingStorefrontIcon className="w-4 h-4" />
                                            Create Restaurant
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add University Modal */}
            <AnimatePresence>
                {showAddUniversityModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
                        >
                            <div className="bg-gradient-to-r from-cheese to-yellow-500 p-6 rounded-t-2xl text-crust">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-bold">Add New University</h2>
                                    <button
                                        onClick={() => setShowAddUniversityModal(false)}
                                        className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        University Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={newUniversity.name}
                                        onChange={(e) => setNewUniversity(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Enter university name"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State *
                                    </label>
                                    <input
                                        type="text"
                                        value={newUniversity.state}
                                        onChange={(e) => setNewUniversity(prev => ({ ...prev, state: e.target.value }))}
                                        placeholder="Enter state"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={newUniversity.isActive}
                                        onChange={(e) => setNewUniversity(prev => ({ ...prev, isActive: e.target.checked }))}
                                        className="rounded border-gray-300 text-cheese focus:ring-cheese"
                                    />
                                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                                        Active university
                                    </label>
                                </div>
                            </div>

                            <div className="flex gap-3 p-6 pt-0">
                                <button
                                    onClick={() => setShowAddUniversityModal(false)}
                                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddUniversity}
                                    disabled={addingUniversity || !newUniversity.name.trim() || !newUniversity.state.trim()}
                                    className="flex-1 py-3 px-4 bg-cheese hover:bg-yellow-500 text-crust font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {addingUniversity ? 'Adding...' : 'Add University'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Edit University Modal */}
            <AnimatePresence>
                {showEditUniversityModal && editUniversity && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
                        >
                            <div className="bg-gradient-to-r from-crust to-pepperoni p-6 rounded-t-2xl text-white">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-bold">Edit University</h2>
                                    <button
                                        onClick={() => {
                                            setShowEditUniversityModal(false);
                                            setEditUniversity(null);
                                        }}
                                        className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        University Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={editUniversity.name}
                                        onChange={(e) => setEditUniversity(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Enter university name"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State *
                                    </label>
                                    <input
                                        type="text"
                                        value={editUniversity.state}
                                        onChange={(e) => setEditUniversity(prev => ({ ...prev, state: e.target.value }))}
                                        placeholder="Enter state"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="editIsActive"
                                        checked={editUniversity.isActive}
                                        onChange={(e) => setEditUniversity(prev => ({ ...prev, isActive: e.target.checked }))}
                                        className="rounded border-gray-300 text-crust focus:ring-cheese"
                                    />
                                    <label htmlFor="editIsActive" className="ml-2 text-sm text-gray-700">
                                        Active university
                                    </label>
                                </div>
                            </div>

                            <div className="flex gap-3 p-6 pt-0">
                                <button
                                    onClick={() => {
                                        setShowEditUniversityModal(false);
                                        setEditUniversity(null);
                                    }}
                                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateUniversity}
                                    disabled={editingUniversity || !editUniversity.name.trim() || !editUniversity.state.trim()}
                                    className="flex-1 py-3 px-4 bg-crust hover:bg-crust text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {editingUniversity ? 'Updating...' : 'Update University'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* View Landmarks Modal */}
            <AnimatePresence>
                {showLandmarksModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
                        >
                            <div className="bg-crust p-6 text-white">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-bold">
                                        Landmarks - {selectedUniversity?.name}
                                    </h2>
                                    <button
                                        onClick={() => setShowLandmarksModal(false)}
                                        className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 max-h-96 overflow-y-auto">
                                {viewLandmarks.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <MapPinIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                        <p>No landmarks found for this university.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {viewLandmarks.map((landmark, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                                            >
                                                <MapPinIcon className="w-5 h-5 text-crust flex-shrink-0" />
                                                <span className="text-gray-800">{landmark}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default UniversityManagement;